class TrendComponent {
   static nextComponentIndex = 0;
   static showAnimationDuration = 800;
   static hideAnimationDuration = 800;
   static scrollThrottle = 1000;

   trendComponentElement;
   componentIndex = TrendComponent.nextComponentIndex++;
   scrollEventTicking = false;
   lastSlide = 0;
   pausePageChangeUntil = null;
   navigationInProgress = false;
   lastScroll = 0;
   touchMoveStart = null;
   isIntersecting = false;

   constructor(trendComponentElement) {
      this.trendComponentElement = trendComponentElement;
      if (this.trendComponentElement) {
          this.setup();
      }
   }

   setup() {
      // Add Event listeners for right navigation arrows
      this.trendComponentElement.querySelector('.trend-navigation li:first-of-type')
         .addEventListener('click', () => this.prev());
      this.trendComponentElement.querySelector('.trend-navigation li:last-of-type')
         .addEventListener('click', () => this.next());
      // Add and setup Toggles and toggled elements
      this.setupToggles();
      this.resetToggleables();
      this.setupTrendCards();
      this.setupProgressNavigation();
      this.setupScrollHandlers();
      this.setupKeyHandlers();
      // Listen to scroll events to animate etc.
      this.trendComponentElement.querySelector('.trend-slides').addEventListener('scroll', (event) => {
         if (!this.scrollEventTicking) {
            window.requestAnimationFrame(() => {
               if (this.pausePageChangeUntil === null || this.currentSlide === this.pausePageChangeUntil) {
                  this.checkSlideChangeAndUpdate();
                  this.pausePageChangeUntil = null;
                  this.triggerAnimations(this.currentSlide);
               }
               this.scrollEventTicking = false;
            });
            this.scrollEventTicking = true;
         }
      });
      // Set last slide to current slide (e.g. after page reload the user might be on a different slide)
      this.lastSlide = this.currentSlide;
      // Reset animations
      this.trendComponentElement.querySelectorAll('.trend-slide').forEach((slideElement, slideIndex) => {
         if (slideIndex.toString() !== this.currentSlide.toString()) {
            slideElement.classList.add('closed-slide');
         }
      });
      this.checkSlideChangeAndUpdate();
   }

   setupTrendCards() {
      this.trendComponentElement.querySelectorAll('.trend-slide').forEach((slideElement, slideIndex) => {
         const trendCardElement = slideElement.querySelector('.trend-information-card');
         const cardToggleBtn = slideElement.querySelector('.btn-plus-minus-toggle');
         if (!trendCardElement || !cardToggleBtn) {
            return;
         }
         trendCardElement.id = this.getTrendCardId(slideIndex);
         cardToggleBtn.ariaControls = this.getTrendCardId(slideIndex);
         cardToggleBtn.addEventListener('click', () => {
            const show = cardToggleBtn.ariaExpanded !== 'true';
            trendCardElement.classList.toggle('open', show);
            trendCardElement.classList.toggle('closed', !show);
            cardToggleBtn.ariaExpanded = show;
         });
      });
   }

   setupProgressNavigation() {
      this.trendComponentElement.querySelectorAll('.trend-progress-navigation-tiles li').forEach((listElement, index) => {
         listElement.addEventListener('click', () => {
            this.goTo(index);
         });
      });
   }

   getTrendCardId(slideIndex) {
      return `trendCard${this.componentIndex}x${slideIndex}`;
   }

   get currentSlide() {
      const grace = 1; // in pixels
      const slideElements = this.trendComponentElement.querySelectorAll('.trend-slide');
      const currentPosition = this.trendComponentElement.querySelector('.trend-slides').scrollTop;
      let currentSlideIndex = 0;
      slideElements.forEach((currentSlide, currentIndex) => {
         if (currentSlide.offsetTop <= currentPosition + grace) {
            currentSlideIndex = currentIndex;
         }
      });
      return currentSlideIndex;
   }

   get totalSlides() {
      return this.trendComponentElement.querySelectorAll('.trend-slide').length;
   }

   next() {
      if (this.currentSlide >= this.totalSlides) {
         this.scrollIntoView();
         return;
      }
      this.goTo(this.currentSlide + 1);
   }

   prev() {
      if (this.currentSlide <= 0) {
         this.scrollIntoView();
         return;
      }
      this.goTo(this.currentSlide - 1);
   }

   goTo(goToSlideIndex) {
      // Do not navigate while navigation is in progress
      if (this.navigationInProgress) {
         return;
      }
      // Acquire navigation lock
      this.navigationInProgress = true;
      // Get elements
      const slideContainer = this.trendComponentElement.querySelector('.trend-slides');
      const slideElement = this.trendComponentElement.querySelectorAll('.trend-slide')[goToSlideIndex];
      if (!slideElement) {
         return;
      }
      // Hide Animation
      this.triggerHideAnimations(this.lastSlide, goToSlideIndex);
      // Scroll to slide
      setTimeout(() => {
         this.pausePageChangeUntil = goToSlideIndex;
         // ScrollToSlide
         if (goToSlideIndex < this.currentSlide) {
            slideContainer.scrollTop = slideElement.offsetHeight * goToSlideIndex;
         } else {
            slideContainer.scrollTop = slideElement.offsetTop;
         }
         this.checkSlideChangeAndUpdate(goToSlideIndex);
         // Show animation
         this.triggerShowAnimations(goToSlideIndex);
         // When animation is done: Release lock
         setTimeout(() => {
            this.navigationInProgress = false;
         }, TrendComponent.showAnimationDuration);
      }, TrendComponent.hideAnimationDuration);
   }

   checkSlideChangeAndUpdate(toSlide = this.currentSlide) {
      const fromSlide = this.lastSlide;
      // Update navigation on the left side
      const navigationItems = this.trendComponentElement.querySelectorAll('.trend-progress-navigation-tiles li');
      navigationItems[fromSlide].classList.add('visited');
      navigationItems[fromSlide].classList.remove('current');
      navigationItems[toSlide].classList.add('current');
      // BREAK here when not moved
      if (this.lastSlide === this.currentSlide) {
         return;
      }
      // Update latest slide
      this.lastSlide = toSlide;
      // Scroll Slide container into view
      this.scrollIntoView();
   }

   triggerHideAnimations() {
      window.requestAnimationFrame(() => {
         this.trendComponentElement.querySelectorAll('.trend-slide').forEach((slideElement) => {
            // Hide slide
            slideElement.classList.add('closed-slide');
            slideElement.classList.remove('current-slide');
         });
      });
   }

   triggerShowAnimations(toSlide) {
      window.requestAnimationFrame(() => {
         this.trendComponentElement.querySelectorAll('.trend-slide').forEach((slideElement, slideIndex) => {
            if (slideIndex.toString() === toSlide.toString()) {
               // Show slide
               slideElement.classList.remove('closed-slide');
               slideElement.classList.add('current-slide');
            }
         });
      });
   }

   triggerAnimations(toSlide) {
      this.triggerHideAnimations();
      this.triggerShowAnimations(toSlide);
   }

   resetToggleables() {
      const slides = this.trendComponentElement.querySelectorAll('.trend-slide');
      slides.forEach((slide, slideIndex) => {
         this.toggleContentOnSlide(slideIndex, '0');
      });
   }

   setupToggles() {
      const slides = this.trendComponentElement.querySelectorAll('.trend-slide');
      slides.forEach((slide, slideIndex) => {
         const toggleableContainers = slide.querySelectorAll('.trend-card-toggles');
         const toggleContainer = slide.querySelector('.trend-card-toggle');
         if (!toggleContainer) {
            return;
         }
         if (toggleableContainers.length > 0 && toggleableContainers[0].children.length > 1) {
            const inputName = `trendSlide${this.componentIndex}x${slideIndex}`;
            const sectionalCardListLabel = toggleContainer.dataset.labelSectionalCardList ?? 'Implications';
            const quoteLabel = toggleContainer.dataset.labelQuote ?? 'Quote';
            toggleContainer.innerHTML = `
              <input type="radio" name="${inputName}" value="0"
                     id="${inputName}cardList" checked />
              <label for="${inputName}cardList">${sectionalCardListLabel}</label>
              <input type="radio" name="${inputName}" value="1"
                     id="${inputName}quote"/>
              <label for="${inputName}quote">${quoteLabel}</label>
             `;
            const radioInputs = slide.querySelectorAll(`[name="${inputName}"]`);
            radioInputs.forEach((radioInput, i) => {
               radioInput.addEventListener('change', () => {
                  this.toggleContentOnSlide(slideIndex, radioInput.value);
               });
            });
         } else {
            toggleContainer.innerHTML = '';
         }
      });
   }

   toggleContentOnSlide(slideIndex, contentIndex, hideClass = 'content-hide') {
      const slides = this.trendComponentElement.querySelectorAll('.trend-slide');
      const slide = slides[slideIndex];
      if (!slide) {
         return;
      }
      const toggleableContainers = slide.querySelectorAll('.trend-card-toggles');
      toggleableContainers.forEach((toggleableContainer) => {
         const children = toggleableContainer.children;
         for (let childIndex = 0; childIndex < children.length; childIndex++) {
            const child = children[childIndex];
            if (childIndex.toString() === contentIndex.toString()) {
               child.classList.add('content-show');
               child.classList.remove('content-hide');
               toggleableContainer.style.minHeight = `${child.offsetHeight}px`;
            } else if (child.classList.contains('content-show')) {
               // Prevent animation on first time
               child.classList.remove('content-show');
               child.classList.add('content-hide');
            }
         }
      });
   }

   scrollIntoView() {
      this.trendComponentElement.scrollIntoView({
         block: 'end',
      });
   }

   getIndexOfTrendId(trendId) {
      const slides = this.trendComponentElement.querySelectorAll('.trend-slide');
      for (const slideIndex in slides) {
         if (slides[slideIndex].dataset?.trendId === trendId) {
            return slideIndex;
         }
      }
   }

   includesTrendId(trendId) {
      return this.getIndexOfTrendId(trendId) !== null;
   }

   goToTrendId(trendId) {
      const slideIndex = this.getIndexOfTrendId(trendId);
      this.goTo(slideIndex);
   }

   get isOnLastSlide() {
      return this.trendComponentElement.querySelectorAll('.trend-slide').length - 1 === this.currentSlide;
   }

   get isOnFirstSlide() {
      return 0 === this.currentSlide;
   }

   setupScrollHandlers() {
      const throttledExecution = (action) => {
         const currentTime = Date.now();
         const shouldRun = currentTime > this.lastScroll + TrendComponent.scrollThrottle;
         this.lastScroll = currentTime;
         if (shouldRun) {
            action();
         }
      };

      const scrollEventHandler = (deltaY, e) => {
         if (deltaY > 0 && !this.isOnLastSlide) {
            e.preventDefault();
            throttledExecution(() => this.next());
         } else if (deltaY < 0 && !this.isOnFirstSlide) {
            e.preventDefault();
            throttledExecution(() => this.prev());
         }
      };

      // Scroll handlers
      this.trendComponentElement.addEventListener('wheel', (e) => {
         scrollEventHandler(e.deltaY, e);
      });
      this.trendComponentElement.addEventListener('mousewheel', (e) => {
         scrollEventHandler(e.deltaY, e);
      });
      this.trendComponentElement.addEventListener('DOMMouseScroll', (e) => {
         scrollEventHandler(e.detail, e);
      });

      // Touch handlers
      this.trendComponentElement.addEventListener('touchmove', (e) => {
         if (!e.touches || e.touches.length === 0) {
            return;
         }
         const diffY = this.touchMoveStart.screenY - e.touches[0].screenY;
         if ((diffY > 0 && !this.isOnLastSlide) || (diffY < 0 && !this.isOnFirstSlide)) {
            e.preventDefault();
         }
      });
      this.trendComponentElement.addEventListener('touchstart', (e) => {
         this.touchMoveStart = e.touches[0];
      });
      this.trendComponentElement.addEventListener('touchend', (e) => {
         if (!e.changedTouches || e.changedTouches.length === 0) {
            return;
         }
         const diffY = this.touchMoveStart.screenY - e.changedTouches[0].screenY;
         if (diffY > 0 && !this.isOnLastSlide) {
            this.next();
         } else if (diffY < 0 && !this.isOnFirstSlide) {
            this.prev();
         }
      });
   }

   setupKeyHandlers() {
      document.addEventListener('keyup', (e) => {
         e.stopPropagation();
         if (this.isIntersecting) {
            if (e.key === 'ArrowUp') {
               e.preventDefault();
               this.prev();
            } else if (e.key === 'ArrowDown') {
               e.preventDefault();
               this.next();
            }
         }
      });
   }
}

const trendComponents = [];

function initializeTrendComponent(trendComponentElement) {
   trendComponents.push(new TrendComponent(trendComponentElement));
}

function initializeTrendComponents(rootElement = document) {

   const trendComponentIntersectionObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
         if (entry.isIntersecting) {
            entry.target.scrollIntoView({
               block: 'end',
            });
         }
         trendComponents.find((trendComponent) => trendComponent.trendComponentElement === entry.target).isIntersecting = entry.isIntersecting;
      });
   }, {
      threshold: 0.15,
      root: rootElement,
   });
   rootElement.querySelectorAll('.trend-component').forEach(
      (trendComponentElement) => {
         initializeTrendComponent(trendComponentElement);
         trendComponentIntersectionObserver.observe(trendComponentElement);
      }
   );

}

window.addEventListener('load', () => {
   initializeTrendComponents();
});

document.addEventListener('scroll-to-trend-slide-id', (scrollRequestEvent) => {
   const scrollToSlideId = scrollRequestEvent.detail?.trendId;
   if (scrollToSlideId) {
      for (const trendComponent of trendComponents) {
         if (trendComponent.includesTrendId(scrollToSlideId)) {
            trendComponent.scrollIntoView();
            trendComponent.goToTrendId(scrollToSlideId);
            break;
         }
      }
   }
});
